import { FC, useState } from 'react'
import { Form, Formik } from 'formik'
import * as Yup from 'yup'
import { useNotification } from 'lib/context/notification.context'
import Input from 'views/components/UI/TextInput'
import { Button } from 'views/components/UI/Buttons'
import createCertificationRegistration, {
  ICertificationRegistrationPayload,
} from 'services/certifications/register'
import validateCertificationCode from 'services/certifications/validateCode'
import {
  Actions,
  Card,
  CheckInput,
  CheckRow,
  ErrorBox,
  Field,
  Grid,
  Page,
  SuccessBox,
  Title,
} from './styled'

type IFormValues = ICertificationRegistrationPayload

type IResponseData = {
  status?: string
  message?: string
}

const validationSchema = Yup.object().shape({
  client_code: Yup.string().trim().required('El código del cliente es obligatorio'),
  certification_name: Yup.string().trim().required('La certificación es obligatoria'),
  full_name: Yup.string().trim().required('El nombre completo es obligatorio'),
  email: Yup.string().email('Correo inválido').required('El correo es obligatorio'),
  document: Yup.string().trim().required('El documento de identidad es obligatorio'),
  job_title: Yup.string().trim().required('El cargo es obligatorio'),
  company: Yup.string().trim().required('La empresa es obligatoria'),
  phone: Yup.string().trim().required('El teléfono es obligatorio'),
  accept_data_processing: Yup.boolean().oneOf([true], 'Debes aceptar el tratamiento de datos'),
  accept_methodology_ownership: Yup.boolean().oneOf([true], 'Debes aceptar la titularidad'),
})

const initialValues: IFormValues = {
  client_code: '',
  certification_name: '',
  full_name: '',
  email: '',
  document: '',
  job_title: '',
  company: '',
  phone: '',
  accept_data_processing: false,
  accept_methodology_ownership: false,
}

const CertificationRegistration: FC = () => {
  const { getWarning, getSuccess } = useNotification()
  const [clientCodeValidated, setClientCodeValidated] = useState<boolean>(false)
  const [clientCodeMessage, setClientCodeMessage] = useState<string>('')
  const [clientCodeError, setClientCodeError] = useState<string>('')
  const [isValidatingCode, setIsValidatingCode] = useState<boolean>(false)

  const handleValidateClientCode = async (clientCode: string) => {
    const trimmedCode = clientCode.trim()

    if (!trimmedCode) {
      setClientCodeValidated(false)
      setClientCodeError('Ingresa un código de cliente para validarlo.')
      setClientCodeMessage('')
      return
    }

    setIsValidatingCode(true)
    setClientCodeError('')
    setClientCodeMessage('')

    const response = await validateCertificationCode(trimmedCode)

    if (response?.status === 'OK' && response.data) {
      setClientCodeValidated(true)
      setClientCodeMessage(
        `Código válido para ${response.data.company_name}. Cupos disponibles: ${response.data.credits_available}.`,
      )
    } else {
      setClientCodeValidated(false)
      setClientCodeError(
        response?.message ||
          'No se pudo validar el código. Revisa la información e intenta nuevamente.',
      )
    }

    setIsValidatingCode(false)
  }

  const handleSubmit = async (values: IFormValues) => {
    if (!clientCodeValidated) {
      getWarning('Primero valida el código del cliente.')
      return
    }

    const response: IResponseData = await createCertificationRegistration(values)

    if (response?.status === 'success' || response?.status === 'OK') {
      getSuccess('Tu inscripción fue registrada correctamente.')
      return
    }

    getWarning(
      response?.message ||
        'No pudimos completar la inscripción. Revisa los datos e intenta nuevamente.',
    )
  }

  return (
    <Page>
      <Card>
        <Title style={{ marginBottom: '20px' }}>Inscripción pública a certificaciones</Title>
        <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
          {({ values, errors, touched, handleChange, handleBlur, isSubmitting }) => (
            <Form>
              <Grid>
                <Field>
                  <Input
                    type="text"
                    name="client_code"
                    label="Código del cliente"
                    value={values.client_code}
                    onChange={(event) => {
                      setClientCodeValidated(false)
                      setClientCodeMessage('')
                      setClientCodeError('')
                      handleChange(event)
                    }}
                    onBlur={handleBlur}
                    error={Boolean(errors.client_code && touched.client_code)}
                    helperText={touched.client_code ? errors.client_code : undefined}
                  />
                  <Actions>
                    <Button
                      type="button"
                      disabled={isValidatingCode || !values.client_code.trim()}
                      onClick={() => handleValidateClientCode(values.client_code)}
                      style={{ padding: '0.8rem 1.2rem' }}
                    >
                      {isValidatingCode ? 'Validando...' : 'Validar código'}
                    </Button>
                  </Actions>
                  {clientCodeMessage ? <SuccessBox>{clientCodeMessage}</SuccessBox> : null}
                  {clientCodeError ? <ErrorBox>{clientCodeError}</ErrorBox> : null}
                </Field>
                <Field>
                  <Input
                    type="text"
                    name="document"
                    label="Documento de identidad"
                    value={values.document}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.document && touched.document)}
                    helperText={touched.document ? errors.document : undefined}
                  />
                </Field>
                <Field>
                  <Input
                    type="text"
                    name="full_name"
                    label="Nombre completo"
                    value={values.full_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.full_name && touched.full_name)}
                    helperText={touched.full_name ? errors.full_name : undefined}
                  />
                </Field>
                <Field>
                  <Input
                    type="text"
                    name="email"
                    label="Correo electrónico"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.email && touched.email)}
                    helperText={touched.email ? errors.email : undefined}
                  />
                </Field>
                <Field>
                  <Input
                    type="text"
                    name="job_title"
                    label="Cargo"
                    value={values.job_title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.job_title && touched.job_title)}
                    helperText={touched.job_title ? errors.job_title : undefined}
                  />
                </Field>
                <Field>
                  <Input
                    type="text"
                    name="company"
                    label="Empresa"
                    value={values.company}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.company && touched.company)}
                    helperText={touched.company ? errors.company : undefined}
                  />
                </Field>
                <Field>
                  <Input
                    type="text"
                    name="phone"
                    label="Teléfono"
                    value={values.phone}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.phone && touched.phone)}
                    helperText={touched.phone ? errors.phone : undefined}
                  />
                </Field>
                <Field style={{ gridColumn: '1 / -1' }}>
                  <Input
                    type="text"
                    name="certification_name"
                    label="Certificación"
                    value={values.certification_name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={Boolean(errors.certification_name && touched.certification_name)}
                    helperText={touched.certification_name ? errors.certification_name : undefined}
                  />
                </Field>
              </Grid>

              <Field style={{ marginTop: '16px' }}>
                <CheckRow>
                  <CheckInput
                    type="checkbox"
                    name="accept_data_processing"
                    checked={values.accept_data_processing}
                    onChange={handleChange}
                  />
                  <span>Acepto el tratamiento de datos personales.</span>
                </CheckRow>
                {touched.accept_data_processing && errors.accept_data_processing ? (
                  <ErrorBox>{errors.accept_data_processing}</ErrorBox>
                ) : null}
              </Field>

              <Field style={{ marginTop: '10px' }}>
                <CheckRow>
                  <CheckInput
                    type="checkbox"
                    name="accept_methodology_ownership"
                    checked={values.accept_methodology_ownership}
                    onChange={handleChange}
                  />
                  <span>Acepto la Titularidad de Metodologías Bluesite.</span>
                </CheckRow>
                {touched.accept_methodology_ownership && errors.accept_methodology_ownership ? (
                  <ErrorBox>{errors.accept_methodology_ownership}</ErrorBox>
                ) : null}
              </Field>

              <Actions>
                <Button
                  type="submit"
                  disabled={isSubmitting || !clientCodeValidated}
                  style={{ padding: '0.95rem 1.4rem' }}
                >
                  {isSubmitting ? 'Registrando...' : 'Inscribirme'}
                </Button>
              </Actions>

              {errors && typeof errors === 'object' && Object.keys(errors).length > 0 ? (
                <ErrorBox>Revisa los campos marcados antes de continuar.</ErrorBox>
              ) : null}
            </Form>
          )}
        </Formik>
      </Card>
    </Page>
  )
}

export default CertificationRegistration
